/**
 * PortfolioFinderWindow Component
 *
 * Pre-configured Finder window for career/portfolio browsing.
 * Displays career history, education, certifications, and products.
 */

import { useMemo } from 'react'
import { FinderWindow, CategoryConfig, ContentItem } from './FinderWindow'
import {
  career,
  education,
  certifications,
  products,
} from '@/generated/content'

interface PortfolioFinderWindowProps {
  windowId: string
  initialPosition?: { x: number; y: number }
}

export function PortfolioFinderWindow({
  windowId,
  initialPosition,
}: PortfolioFinderWindowProps) {
  // Build categories from content
  const categories: CategoryConfig[] = useMemo(
    () => [
      { id: 'career', name: 'Career', icon: '💼', description: 'Work history' },
      { id: 'education', name: 'Education', icon: '🎓', description: 'Academic background' },
      { id: 'certifications', name: 'Certifications', icon: '📜', description: 'Professional credentials' },
      { id: 'products', name: 'Products', icon: '🚀', description: 'Things I built' },
    ],
    []
  )

  // Build items from all content sources
  const allItems: ContentItem[] = useMemo(() => {
    const items: ContentItem[] = []

    // Add career entries
    for (const entry of career) {
      items.push({
        ...entry,
        name: entry.company,
        category: 'career',
      })
    }

    // Add education entries
    for (const entry of education) {
      items.push({
        ...entry,
        name: entry.institution,
        category: 'education',
      })
    }

    // Add certifications
    for (const entry of certifications) {
      items.push({
        ...entry,
        category: 'certifications',
      })
    }

    // Add products (if any)
    for (const entry of products) {
      items.push({
        ...entry,
        category: 'products',
      })
    }

    return items
  }, [])

  // Get items for a specific category
  const getCategoryItems = (categoryId: string) => {
    return allItems.filter((item) => item.category === categoryId)
  }

  return (
    <FinderWindow
      windowId={windowId}
      title="Portfolio"
      icon="📁"
      initialPosition={initialPosition}
      categories={categories}
      items={allItems}
      getCategoryItems={getCategoryItems}
      defaultView="icon"
      showSidebar={true}
      showPreview={false}
      storageKey="portfolio-finder"
    />
  )
}

export default PortfolioFinderWindow
